import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { auth, googleProvider } from "../../config/firebase";
import { signInWithPopup } from "firebase/auth";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import logo from "../../assets/logoProd.png";
import logoLight from "../../assets/logoProdLight.png";
import { useIsMobile } from "../../App";
import analyticsService from "../../services/AnalyticsService";

const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { login, loginWithGoogle } = useAuth();
  const { theme } = useTheme();
  const { t, changeLanguage } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Перевіряємо, чи є повідомлення про успішне підтвердження email
    if (location.state?.message) {
      // Показуємо успішне повідомлення
      setErrors({ success: location.state.message });
    }
  }, [location.state]);

  // Фікс для мобільних: не дозволяємо сторінці прокручуватися після закриття клавіатури
  useEffect(() => {
    if (!isMobile) return;

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: "instant" });
      if (document.body) {
        document.body.scrollTop = 0;
      }
    };

    const handleResize = () => {
      setTimeout(scrollToTop, 150);
    };

    const handleBlur = (e) => {
      if (e.target.tagName === "INPUT") {
        setTimeout(scrollToTop, 150);
      }
    };

    let visualViewportHandler = null;
    if (window.visualViewport) {
      visualViewportHandler = () => {
        setTimeout(scrollToTop, 150);
      };
      window.visualViewport.addEventListener("resize", visualViewportHandler);
    }

    window.addEventListener("resize", handleResize);
    document.addEventListener("blur", handleBlur, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("blur", handleBlur, true);
      if (visualViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          visualViewportHandler
        );
      }
    };
  }, [isMobile]);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = t("emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t("emailInvalid");
    }

    if (!password.trim()) {
      newErrors.password = t("passwordRequired");
    } else if (password.length < 6) {
      newErrors.password = t("passwordMinLength");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const result = await login(email, password);

      // After successful login, sync language to backend
      if (result.success) {
        // Log successful login to analytics
        analyticsService.logLogin('email');
        
        const lang = localStorage.getItem("language");
        if (lang) {
          changeLanguage(lang); // This will send to backend
        }
      }

      // Обробляємо різні типи помилок
      if (!result.success && result.error) {
        if (result.status === 403) {
          // Акаунт видалено
          analyticsService.logError('login_failed', 'account_deleted');
          setErrors({
            general: t("accountDeleted")
          });
        } else if (result.status === 401) {
          // Неправильний email або пароль
          analyticsService.logError('login_failed', 'invalid_credentials');
          setErrors({ general: t("invalidLoginData") });
        } else if (result.error.includes("Network Error")) {
          // Проблеми з мережею
          analyticsService.logError('login_failed', 'network_error');
          setErrors({ general: t("badGateway") || t("loginFailed") });
        } else {
          // Інші помилки
          analyticsService.logError('login_failed', result.error || 'unknown_error');
          setErrors({ general: result.error || t("loginFailed") });
        }
      }
    } catch (error) {
      analyticsService.logError('login_failed', 'exception');
      setErrors({ general: t("loginFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setErrors({});
    try {
      // Analytics: start Google login (web)
      try { analyticsService.logEvent('google_login_start_web', { ts: new Date().toISOString() }); } catch (_) {}
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      // Build payload expected by backend
      const fullName = result.user.displayName || "";
      const [firstNameRaw = "", ...restName] = fullName.trim().split(" ");
      const first_name = firstNameRaw;
      const last_name = restName.join(" ");
      const language =
        localStorage.getItem("language") ||
        (navigator.language || "uk").split("-")[0] ||
        "uk";
      const searchParams = new URLSearchParams(location.search);
      const campaign_code =
        searchParams.get("campaign_code") ||
        searchParams.get("campaign") ||
        localStorage.getItem("campaign_code") ||
        undefined;

      const payload = {
        google_id: result.user.uid,
        id_token: idToken,
        email: result.user.email,
        first_name,
        last_name,
        language,
        campaign_code,
      };

      const loginResult = await loginWithGoogle(payload);

      if (!loginResult.success) {
        // Analytics: error on backend exchange (web)
        try {
          analyticsService.logEvent('google_login_error_web', {
            stage: 'exchange',
            status: loginResult.status,
            detail: loginResult.detail || loginResult.error,
            ts: new Date().toISOString(),
          });
        } catch (_) {}
        setErrors({ general: loginResult.error || t("loginFailed") });
      } else {
        // Sync language after auth
        const lang = localStorage.getItem("language");
        if (lang) {
          changeLanguage(lang);
        }
        // Analytics: success (web)
        try {
          analyticsService.logEvent('google_login_success_web', {
            email: result?.user?.email,
            uid: result?.user?.uid,
            ts: new Date().toISOString(),
          });
        } catch (_) {}
      }
    } catch (error) {
      // Analytics: error on popup/auth (web)
      try {
        analyticsService.logEvent('google_login_error_web', {
          stage: 'popup',
          code: error?.code,
          message: error?.message,
          ts: new Date().toISOString(),
        });
      } catch (_) {}
      setErrors({ general: t("loginFailed") });
    } finally {
      setLoading(false);
    }
  };

  // const handleForgotPassword = () => {
  //   navigate('/forgot-password');
  // };

  const containerStyle = {
    minHeight: isMobile ? "100dvh" : "100vh",
    height: isMobile ? "100dvh" : "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: theme.background,
    padding: isMobile ? "12px 0" : "32px 0",
    boxSizing: "border-box",
    overflowY: isMobile ? "auto" : "visible",
    WebkitOverflowScrolling: "touch",
  };
  const formStyle = {
    width: isMobile ? "96vw" : 400,
    maxWidth: 400,
    background: theme.surface,
    borderRadius: isMobile ? 8 : 16,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    padding: isMobile ? "20px 16px" : "32px",
    boxSizing: "border-box",
  };

  const logoStyles = {
    display: "block",
    margin: isMobile ? "0 auto 12px auto" : "0 auto 24px auto",
    width: isMobile ? "200px" : "250px",
    height: "auto",
  };

  const titleStyles = {
    fontSize: isMobile ? "22px" : "26px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: isMobile ? "20px" : "32px",
    color: theme.text,
  };

  const formStyles = {
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "16px" : "20px",
    marginBottom: isMobile ? "16px" : "24px",
  };

  const linkStyles = {
    textAlign: "center",
    color: theme.primary,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "16px",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const successStyles = {
    color: theme.success || "#4caf50",
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const isDark = theme.isDark;
  const logoToShow = isDark ? logoLight : logo;

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        <img src={logoToShow} alt="Logo" style={logoStyles} />
        <h1 style={titleStyles}>{t("welcome")}</h1>

        {errors.general && <div style={errorStyles}>{errors.general}</div>}
        {errors.success && <div style={successStyles}>{errors.success}</div>}

        <div style={formStyles}>
          <Input
            label={t("email")}
            type="email"
            placeholder={t("email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.email}
            required
            autoComplete="email"
          />

          <Input
            label={t("password")}
            type="password"
            placeholder={t("password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.password}
            required
            autoComplete="current-password"
          />

          {/* <div style={{
            textAlign: 'center',
            marginBottom: '16px',
            fontSize: '14px',
            color: theme.textSecondary
          }}>
            <span
              onClick={handleForgotPassword}
              style={{
                color: theme.primary,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: '500'
              }}
            >
              {t('forgotPassword')}
            </span>
          </div>*/}

          <Button
            className="login-button"
            onClick={handleLogin}
            loading={loading}
            disabled={!email || !password}
            size={isMobile ? "small" : "large"}
            style={{
              width: "100%",
              height: isMobile ? "44px" : "48px",
            }}
          >
            {t("login")}
          </Button>

          <Button
            className="google-login-button"
            onClick={handleGoogleLogin}
            loading={loading}
            size={isMobile ? "small" : "large"}
            style={{
              width: "100%",
              height: isMobile ? "44px" : "48px",
              backgroundColor: "#FFFFFF",
              color: "#3c4043",
              border: "1px solid #dadce0",
              borderRadius: "4px",
              boxShadow: "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px",
            }}
            aria-label="Sign in with Google"
            icon={
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#EA4335" d="M17.64 9.204c0-.64-.057-1.252-.164-1.836H9v3.472h4.844a4.14 4.14 0 0 1-1.797 2.715v2.255h2.908c1.703-1.569 2.685-3.879 2.685-6.606z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.468-.804 5.958-2.19l-2.908-2.255c-.806.54-1.837.861-3.05.861-2.345 0-4.331-1.582-5.04-3.708H.957v2.33A9 9 0 0 0 9 18z"/>
                <path fill="#4285F4" d="M3.96 10.708A5.4 5.4 0 0 1 3.667 9c0-.593.102-1.168.293-1.708V4.962H.957A9 9 0 0 0 0 9c0 1.461.35 2.84.957 4.038l3.003-2.33z"/>
                <path fill="#FBBC05" d="M9 3.58c1.32 0 2.508.454 3.44 1.346l2.58-2.58C13.464.9 11.43 0 9 0A9 9 0 0 0 .957 4.962l3.003 2.33C4.668 5.166 6.654 3.58 9 3.58z"/>
              </svg>
            }
          >
            {t("continueWithGoogle") || "Увійти через Google"}
          </Button>

        </div>

        <div style={linkStyles} onClick={() => navigate("/register")}>
          {t("dontHaveAccount")} <strong>{t("register")}</strong>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
